import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { ICONS } from '../constants';

interface RenameBiddingModalProps {
  isOpen: boolean;
  currentName: string;
  onClose: () => void;
  onRename: (newName: string) => void;
}

const RenameBiddingModal: React.FC<RenameBiddingModalProps> = ({
  isOpen,
  currentName,
  onClose,
  onRename,
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState(currentName);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setName(currentName);
      setError(null);
      // Focus and select text after modal opens
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 100);
    }
  }, [isOpen, currentName]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const trimmedName = name.trim();
    
    if (!trimmedName) {
      setError(t('bidding.nameRequired'));
      return;
    }
    
    if (trimmedName === currentName) {
      onClose();
      return;
    }
    
    onRename(trimmedName);
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div 
        className="bg-gray-800 rounded-lg shadow-2xl max-w-md w-full mx-4 border border-gray-700"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <div className="flex items-center space-x-3">
            <ICONS.Edit className="w-5 h-5 text-indigo-400" />
            <h2 className="text-xl font-semibold text-white">
              {t('contextMenu.rename')}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors p-1 rounded-lg hover:bg-gray-700"
          >
            <ICONS.X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-2">
            <label htmlFor="bidding-name" className="block text-sm font-medium text-gray-300">
              {t('bidding.name')}
            </label>
            <input
              ref={inputRef}
              id="bidding-name"
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setError(null);
              }}
              className={`w-full px-4 py-3 bg-gray-900 border rounded-lg text-white placeholder-gray-500 
                focus:outline-none focus:ring-2 transition-all ${
                  error 
                    ? 'border-red-500 focus:ring-red-500/50' 
                    : 'border-gray-700 focus:border-indigo-500 focus:ring-indigo-500/50'
                }`}
              placeholder={t('bidding.namePlaceholder')}
              autoComplete="off"
              maxLength={255}
            />
            {error && (
              <p className="text-sm text-red-400 flex items-center space-x-1">
                <ICONS.AlertCircle className="w-4 h-4" />
                <span>{error}</span>
              </p>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-300 hover:text-white bg-gray-700 hover:bg-gray-600 
                rounded-lg transition-colors font-medium"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={!name.trim() || name.trim() === currentName}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg 
                transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed 
                flex items-center space-x-2"
            >
              <ICONS.Check className="w-4 h-4" />
              <span>{t('common.save')}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RenameBiddingModal;

